import { TZDate, tz, tzOffset } from '@date-fns/tz';
import { format } from 'date-fns';
import { type Clock, systemClock, toDate } from './clock.js';

/** An instant: a Date or an ISO-8601 string with `Z`/offset. */
export type Instant = Date | string;

/** A calendar date `YYYY-MM-DD` with no time zone (best-before, trading day). */
export type PlainDate = string;

/** The account timezone Crumble-shaped accounts use. Accounts store their own. */
export const DEFAULT_TIME_ZONE = 'Africa/Johannesburg';

const PLAIN_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
// `HH:mm`, or `HH:mm:ss` as Postgres returns a `time` column.
const CUTOFF_RE = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;
const DAY_MS = 86_400_000;

const validZones = new Set<string>();

/** True when `zone` is an IANA time zone this runtime knows. */
export function isValidTimeZone(zone: string): boolean {
  if (validZones.has(zone)) return true;
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
  } catch {
    return false;
  }
  validZones.add(zone);
  return true;
}

function assertZone(zone: string): void {
  if (!isValidTimeZone(zone)) throw new RangeError(`Unknown time zone: ${zone}`);
}

/** True when `text` is a real calendar date in `YYYY-MM-DD` form. */
export function isPlainDate(text: string): boolean {
  const m = PLAIN_DATE_RE.exec(text);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

function plainDateToUtcMs(date: PlainDate): number {
  if (!isPlainDate(date)) throw new RangeError(`Not a YYYY-MM-DD date: ${JSON.stringify(date)}`);
  return Date.parse(`${date}T00:00:00Z`);
}

/** Adds (or subtracts) whole calendar days to a plain date. */
export function addDays(date: PlainDate, days: number): PlainDate {
  if (!Number.isSafeInteger(days)) throw new RangeError('days must be an integer');
  return new Date(plainDateToUtcMs(date) + days * DAY_MS).toISOString().slice(0, 10);
}

/** Calendar days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: PlainDate, to: PlainDate): number {
  return Math.round((plainDateToUtcMs(to) - plainDateToUtcMs(from)) / DAY_MS);
}

function formatIn(instant: Instant, zone: string, pattern: string): string {
  assertZone(zone);
  return format(toDate(instant), pattern, { in: tz(zone) });
}

// The zone's UTC offset, looked up once per zone and quarter hour. The sales replay asks
// for the local date of tens of thousands of instants (every movement's trading day, every
// FEFO shelf-life test); formatting each one through date-fns cost seconds of CPU per
// replay. Offsets change only at a transition, months apart: a quarter hour whose two ends
// have the same offset has it throughout, and one whose ends differ (it holds the
// transition) is looked up instant by instant. Same offsets as date-fns (`tzOffset`), so
// the same dates.
const OFFSET_BUCKET_MS = 15 * 60_000;
const OFFSET_CACHE_MAX = 100_000;
const offsetBuckets = new Map<string, number>();

function offsetMinutes(zone: string, ms: number): number {
  const bucket = Math.floor(ms / OFFSET_BUCKET_MS);
  const key = `${zone}\u0000${bucket}`;
  let offset = offsetBuckets.get(key);
  if (offset === undefined) {
    const start = bucket * OFFSET_BUCKET_MS;
    const first = tzOffset(zone, new Date(start));
    const last = tzOffset(zone, new Date(start + OFFSET_BUCKET_MS - 1));
    offset = first === last ? first : Number.NaN;
    if (offsetBuckets.size >= OFFSET_CACHE_MAX) offsetBuckets.clear();
    offsetBuckets.set(key, offset);
  }
  return Number.isNaN(offset) ? tzOffset(zone, new Date(ms)) : offset;
}

/**
 * The local wall clock of `instant` in `zone`: its date and minutes since local midnight.
 * Fast path for whole-minute offsets and four-digit years (everything real); anything
 * else goes through date-fns.
 */
function localClock(instant: Instant, zone: string): { date: PlainDate; minutes: number } {
  assertZone(zone);
  const ms = toDate(instant).getTime();
  const offset = offsetMinutes(zone, ms);
  if (Number.isInteger(offset)) {
    const local = new Date(ms + offset * 60_000);
    const year = local.getUTCFullYear();
    if (year >= 1000 && year <= 9999) {
      return {
        date: local.toISOString().slice(0, 10),
        minutes: local.getUTCHours() * 60 + local.getUTCMinutes(),
      };
    }
  }
  const [date, hh, mm] = formatIn(instant, zone, 'yyyy-MM-dd HH mm').split(' ') as [
    string,
    string,
    string,
  ];
  return { date, minutes: Number(hh) * 60 + Number(mm) };
}

/** The local calendar date of `instant` in `zone`, as `YYYY-MM-DD`. */
export function localDate(instant: Instant, zone: string): PlainDate {
  return localClock(instant, zone).date;
}

/** True when `text` is a trading-day cut-off: `HH:mm` or `HH:mm:00` (00:00–23:59). */
export function isCutoff(text: string): boolean {
  const m = CUTOFF_RE.exec(text);
  return m !== null && (m[3] === undefined || m[3] === '00');
}

/**
 * A cut-off as `HH:mm`. Accepts `HH:mm:ss` (how Postgres returns a `time`); cut-offs are
 * whole minutes, so seconds must be `00`.
 */
export function normaliseCutoff(text: string): string {
  const m = CUTOFF_RE.exec(text);
  if (!m || (m[3] !== undefined && m[3] !== '00')) {
    throw new RangeError(`Cut-off must be HH:mm (00:00-23:59), got ${text}`);
  }
  return `${m[1]}:${m[2]}`;
}

/**
 * The trading day an instant belongs to (lesson 10). Business before the local `cutoff`
 * (`HH:mm`, or `HH:mm:00` as stored) belongs to the previous local day. With cutoff
 * `00:00` the trading day is the local date; a sale at 00:30 SAST with cutoff `03:00`
 * belongs to the day before.
 */
export function tradingDay(instant: Instant, zone: string, cutoff = '00:00'): PlainDate {
  const [ch, cm] = normaliseCutoff(cutoff).split(':');
  const cutoffMinutes = Number(ch) * 60 + Number(cm);
  const { date, minutes } = localClock(instant, zone);
  return minutes < cutoffMinutes ? addDays(date, -1) : date;
}

/** Options for {@link businessToday}. */
export interface BusinessTodayOptions {
  /** The instant to judge, or a clock to read it from (default: the system clock). */
  now?: Instant | Clock;
  /**
   * The account's trading-day cut-off (`HH:mm`). Leave it out (or `00:00`) for the local
   * calendar date, which is what best-before judgements use; pass it for "today's trading
   * day" (sales, replays), which starts at the cut-off.
   */
  cutoff?: string;
}

/**
 * "Today" for the business: the calendar date in the **business's** zone (the account's
 * IANA zone; SAST by default), never the device's. Every UI "today", "days left",
 * "yesterday" and default date range starts here: a browser in Dubai at 01:56 on the 29th
 * is at 23:56 on the 28th in Johannesburg, and so is the business (B-050).
 */
export function businessToday(
  zone: string = DEFAULT_TIME_ZONE,
  options: BusinessTodayOptions = {},
): PlainDate {
  const { now = systemClock, cutoff = '00:00' } = options;
  const instant = typeof now === 'string' || now instanceof Date ? now : now.now();
  return tradingDay(instant, zone, cutoff);
}

/**
 * The instant a local wall-clock time (`HH:mm`) on a calendar date denotes in `zone`, as an
 * ISO-8601 UTC string: `localInstant('2026-06-01', '03:00', 'Africa/Johannesburg')` →
 * `2026-06-01T01:00:00.000Z`. A time that does not exist locally (a DST gap) moves forward.
 */
export function localInstant(date: PlainDate, time: string, zone: string): string {
  assertZone(zone);
  const ms = plainDateToUtcMs(date);
  const [hh, mm] = normaliseCutoff(time).split(':').map(Number) as [number, number];
  const day = new Date(ms);
  const local = new TZDate(
    day.getUTCFullYear(),
    day.getUTCMonth(),
    day.getUTCDate(),
    hh,
    mm,
    0,
    0,
    zone,
  );
  return new Date(local.getTime()).toISOString();
}

/**
 * True when the stock is past its best-before at `instant`: the **local** date in `zone`
 * is after `bestBefore`. The best-before day itself is still fine.
 */
export function isPastBestBefore(bestBefore: PlainDate, instant: Instant, zone: string): boolean {
  return daysUntil(bestBefore, instant, zone) < 0;
}

/**
 * Whole local calendar days from `instant` (in `zone`) until `bestBefore`:
 * 0 on the best-before day, 1 the day before, negative once past.
 */
export function daysUntil(bestBefore: PlainDate, instant: Instant, zone: string): number {
  return daysBetween(localDate(instant, zone), bestBefore);
}

/** `27 Sep` — an instant's local date in `zone`. */
export function formatDate(instant: Instant, zone: string): string {
  return formatIn(instant, zone, 'd MMM');
}

/** `27 Sep 14:05` (24-hour) in `zone`. */
export function formatDateTime(instant: Instant, zone: string): string {
  return formatIn(instant, zone, 'd MMM HH:mm');
}

/** `14:05` (24-hour) in `zone`. */
export function formatTime(instant: Instant, zone: string): string {
  return formatIn(instant, zone, 'HH:mm');
}

/** `27 Sep` for a plain calendar date (no zone conversion: a best-before is a date). */
export function formatPlainDate(date: PlainDate): string {
  return format(new Date(plainDateToUtcMs(date)), 'd MMM', { in: tz('UTC') });
}
